import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  X, Sparkles, Target, Clock, Zap, Layers, ChevronRight, Bookmark, EyeOff, 
  Flame, Calendar, AlertCircle, RotateCcw, Moon, MessageCircle, Brain,
  ArrowRight, Lightbulb, TrendingUp, TrendingDown, Archive, List
} from 'lucide-react';
import { getImageUrl } from '../../utils/imageProxy';
import { getThumbnailUrl } from '../../utils/thumbnailFallbacks';
import suggestionsService from '../../services/suggestionsService';
import focusService from '../../services/focusService';
import { useAuth } from '../../contexts/AuthContext';

// V8 - 5 Insight Types (3 from items + 2 from focuses/themes)
const SUGGESTION_CONFIG = {
  deep_dive: {
    icon: Layers,
    color: 'indigo',
    gradient: 'from-indigo-500 to-purple-600',
    bgGradient: 'from-indigo-50 to-purple-50',
  },
  evening_pattern: {
    icon: Moon,
    color: 'violet',
    gradient: 'from-violet-500 to-purple-600',
    bgGradient: 'from-violet-50 to-purple-50',
  },
  emerging_direction: {
    icon: Sparkles,
    color: 'teal',
    gradient: 'from-teal-500 to-cyan-600',
    bgGradient: 'from-teal-50 to-cyan-50',
  },
  neglected_focus: {
    icon: Clock,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-600',
    bgGradient: 'from-amber-50 to-orange-50',
  },
  time_gaps: {
    icon: TrendingDown,
    color: 'rose',
    gradient: 'from-rose-500 to-pink-600',
    bgGradient: 'from-rose-50 to-pink-50',
  },
  // Fallback
  none: {
    icon: Sparkles,
    color: 'gray',
    gradient: 'from-gray-400 to-gray-500',
    bgGradient: 'from-gray-50 to-slate-50',
  },
};

// Generate in-depth insights and action points based on insight type
function getDetailedInsights(suggestion) {
  const typeKey = typeof suggestion.type === 'string' 
    ? suggestion.type 
    : (suggestion.type?.id || 'none');
  const data = suggestion.data || {};
  
  const insightDetails = {
    deep_dive: {
      summary: `You collected ${(data.microTopics || []).join(', ')}. ${data.count || 12}+ items focusing on these patterns. This is not casual scrolling—you are shaping a clear system or preparing for a future output. These items cluster around a specific direction.`,
      actionPoints: [
        'Create a Focus to see the full collection',
        'Break items into sub-themes or phases',
        'Extract the 5 most valuable patterns',
        'Turn this into a moodboard, plan, or output',
        'Ask Swirl to synthesize the key insights'
      ],
      nextSteps: 'Organize this deep collection into something actionable you can build from.'
    },
    evening_pattern: {
      summary: `${data.percentage || 60}% of your saves happen ${data.timeWindow || 'in the evening'}. This is not random—this is when your brain is most engaged. You use this window to plan, refine, and make decisions. This pattern reveals your natural creative energy zone.`,
      actionPoints: [
        'Create a Focus to capture evening patterns',
        'Shape an evening flow or routine',
        'Use this window for planning or inspiration',
        'Block distractions during peak hours',
        'Ask Swirl to design an evening routine for you'
      ],
      nextSteps: 'Turn this natural timing into a structured routine you can rely on every day.'
    },
    emerging_direction: {
      summary: data.isPlaceholder 
        ? (data.reason === 'insufficient_data' 
            ? `You have ${data.itemCount} items saved in the last 3 weeks. Emerging Direction insights appear when you save 3+ related items about a new topic. Keep saving content that interests you, and this card will detect early patterns as they form.`
            : `You have ${data.itemCount} items saved, but they are spread across ${data.topicsFound || 'many'} different topics. Emerging Direction insights appear when items cluster together. Your saves are diverse right now, which means no single direction is forming yet. This changes as you save more in areas that interest you.`)
        : `You saved ${(data.microTopics || []).join(', ')}. ${data.count || 3} items in the past ${data.weeks || 3} weeks. Early patterns like this often signal a new interest forming. This cluster is worth tracking to see where it leads.`,
      actionPoints: data.isPlaceholder
        ? [
            'Save 5-10 items in an area that interests you',
            'Use the browser extension to capture content quickly',
            'Focus your saves on 2-3 themes you want to explore',
            'Check back in a few days as patterns form'
          ]
        : [
            'Create a Focus to group these items',
            'Save a few more items to build context',
            'Note what triggered your interest here',
            'Group items into a plan or prep list',
            'Ask Swirl where this direction might lead'
          ],
      nextSteps: data.isPlaceholder
        ? 'Keep saving content consistently, and patterns will emerge automatically.'
        : 'Let this develop naturally, but track it so you can act when the pattern solidifies.'
    },
    neglected_focus: {
      summary: `Your Focus "${data.focusTitle || 'this focus'}" was last active ${data.daysSinceActive || 14} days ago. When focuses go quiet for this long, it usually means either the project stalled or priorities shifted. This is worth revisiting to either archive it or revive it with fresh momentum.`,
      actionPoints: [
        'Open the Focus to review what was there',
        'Check if the mission still matters to you',
        'Archive it if priorities have shifted',
        'Revive it with 3-5 new saves or actions',
        'Ask Swirl to help build a revival plan'
      ],
      nextSteps: 'Decide: archive this focus or revive it with specific actions.'
    },
    time_gaps: {
      summary: `You saved ${data.totalSaves || 0} items about "${data.topic || 'this topic'}" between ${data.firstSave ? new Date(data.firstSave).toLocaleDateString() : 'some time ago'} and ${data.lastSave ? new Date(data.lastSave).toLocaleDateString() : 'recently'}, but then stopped. It's been ${data.daysSinceLastSave || 21} days since your last save. When a theme goes quiet for this long after consistent attention, it's worth asking: did priorities shift, or is this something you want to revive?`,
      actionPoints: [
        'Review the items you saved on this topic',
        'Filter Swirlspace by this theme to see patterns',
        'Decide if this theme is still relevant',
        'Create a Focus if you want to revive it',
        'Ask Swirl why this might have stopped'
      ],
      nextSteps: 'Clarify whether this theme is dormant by design or accidentally forgotten.'
    },
  };
  
  return insightDetails[typeKey] || {
    summary: suggestion.reason || 'This insight is based on specific micro-patterns in your recent saves and behavior.',
    actionPoints: [
      'Review the related items to understand the pattern',
      'Create a Focus page to organize this theme',
      'Ask Swirl to analyze this collection deeper',
      'Set a reminder to act on this within 48 hours'
    ],
    nextSteps: 'Take one concrete action on this insight before the momentum fades.'
  };
}

export default function SmartSuggestionModal({ 
  isOpen, 
  onClose, 
  suggestion,
  allSuggestions = [],
  relatedItems = [],
  onPin = null,
  onUnpin = null,
  isPinned = false,
  onDismiss = null,
  onSelectSuggestion = null,
  onRefresh = null,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showRelatedItems, setShowRelatedItems] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && suggestion) {
      suggestionsService.logInsightModalOpen(suggestion);
    }
  }, [isOpen, suggestion]);

  if (!isOpen || !suggestion) return null;

  const typeKey = typeof suggestion.type === 'string' 
    ? suggestion.type 
    : (suggestion.type?.id || 'none');
  const config = SUGGESTION_CONFIG[typeKey] || SUGGESTION_CONFIG.none;
  const Icon = config.icon;
  const isAIPredictions = suggestion.isAIPredictions || typeKey === 'ai_predictions';
  
  const otherSuggestions = allSuggestions.filter(s => {
    const sKey = typeof s.type === 'string' ? s.type : (s.type?.id || 'none');
    return sKey !== typeKey && sKey !== 'none' && !s.isAIPredictions;
  });

  const suggestionActions = suggestion.actions || suggestion.type?.actions || [];
  const createFocusAction = suggestionActions.find(a => a.type === 'create_focus');
  const askSwirlAction = suggestionActions.find(a => a.type === 'ask_swirl');
  const detailedInsights = getDetailedInsights(suggestion);

  // ============================================
  // ACTION HANDLERS
  // ============================================

  const handleCreateFocus = () => {
    console.log('[Insights] Create Focus:', typeKey);
    navigate('/swirlspace');
    onClose();
  };

  const handleAskSwirl = () => {
    const prompt = askSwirlAction?.askSwirlPrompt || 'Help me explore this insight further.';
    console.log('[Insights] Ask Swirl:', typeKey, prompt);
    navigate(`/ai-chat?prompt=${encodeURIComponent(prompt)}`);
    onClose();
  };

  const handleViewPredictions = () => {
    console.log('[Insights] View Predictions (coming soon)');
    // Placeholder - will open predictions modal when ready
  };

  // ============================================
  // NEGLECTED FOCUS ACTION HANDLERS
  // ============================================

  const handleOpenFocus = () => {
    if (suggestion.data?.focusId) {
      navigate(`/focus/${suggestion.data.focusId}`);
      onClose();
    }
  };

  const handleArchiveFocus = async () => {
    if (!suggestion.data?.focusId) return;
    
    setActionLoading(true);
    try {
      await focusService.archiveFocus(suggestion.data.focusId);
      console.log('[Insights] Archived focus:', suggestion.data.focusId);
      
      // Dismiss the insight and close modal
      onDismiss?.(suggestion.id);
      onClose();
      
      // Refresh Smart Insights (no page reload)
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error archiving focus:', error);
      alert('Failed to archive focus. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMakePlan = () => {
    if (!suggestion.data?.focusId || !suggestion.data?.focusTitle) return;
    
    // Navigate to Ask Swirl with focus context
    const prompt = `Help me turn my focus '${suggestion.data.focusTitle}' into a simple plan.`;
    navigate(`/ai-chat?prompt=${encodeURIComponent(prompt)}&focusId=${suggestion.data.focusId}`);
    onClose();
  };

  // ============================================
  // TIME GAPS ACTION HANDLERS
  // ============================================

  const handleViewRelatedItems = () => {
    setShowRelatedItems(!showRelatedItems);
  };

  const handleCreateFocusFromTheme = async () => {
    if (!user?.id || !suggestion.data?.topic) return;
    
    setActionLoading(true);
    try {
      const focus = await focusService.createFocus(user.id, {
        title: suggestion.data.topic,
        description: `Revived from time gap: ${suggestion.data.totalSaves} items saved`,
      });
      
      console.log('[Insights] Created focus from theme:', focus.id);
      
      // Navigate to the new focus
      navigate(`/focus/${focus.id}`);
      onClose();
    } catch (error) {
      console.error('Error creating focus from theme:', error);
      alert('Failed to create focus. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAskSwirlAboutGap = () => {
    const topic = suggestion.data?.topic || 'this topic';
    const days = suggestion.data?.daysSinceLastSave || 21;
    const prompt = `I stopped engaging with "${topic}" for ${days} days. Help me understand why and what to do next.`;
    
    // Attach related items as context
    const itemIds = (suggestion.relatedItems || relatedItems).map(item => item.id).join(',');
    navigate(`/ai-chat?prompt=${encodeURIComponent(prompt)}&itemIds=${itemIds}`);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden pointer-events-auto flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`relative px-5 pt-5 pb-4 bg-gradient-to-br ${config.bgGradient} flex-shrink-0`}>
                <button
                  onClick={onClose}
                  className="absolute top-3 right-3 p-1.5 rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700 transition-colors shadow-sm"
                >
                  <X size={14} />
                </button>
                
                <div className="flex items-start gap-3 pr-8">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <Icon size={20} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 mb-0.5">
                      {suggestion.title}
                    </h2>
                    <p className="text-sm text-gray-600 leading-snug">
                      {suggestion.subtitle}
                    </p>
                  </div>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
                
                {/* Why You're Seeing This - Enhanced */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Lightbulb size={11} className="text-amber-500" />
                    Why this matters
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {detailedInsights.summary}
                  </p>
                </div>

                {/* Action Points */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <TrendingUp size={11} className="text-emerald-500" />
                    What you can do
                  </h3>
                  <ul className="space-y-1.5">
                    {detailedInsights.actionPoints.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <ChevronRight size={12} className="text-purple-400 mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Next Step */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">Next step</p>
                  <p className="text-sm text-gray-800">{detailedInsights.nextSteps}</p>
                </div>

                {/* Related Items Toggle (Time Gaps only) */}
                {typeKey === 'time_gaps' && showRelatedItems && (
                  <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                    <h3 className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2">
                      Related items from this theme
                    </h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(suggestion.relatedItems || relatedItems).slice(0, 10).map((item, i) => {
                        const thumbUrl = getThumbnailUrl(item) || item.thumbnail_url || item.image_url || item.url;
                        const proxiedUrl = thumbUrl ? getImageUrl({ image: thumbUrl, thumbnail_url: thumbUrl }) : null;
                        
                        return (
                          <div key={item.id || i} className="flex items-start gap-2 bg-white rounded-lg p-2">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-purple-100 to-violet-100">
                              {proxiedUrl ? (
                                <img src={proxiedUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-purple-400">
                                  <Layers size={12} />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 line-clamp-1">{item.title || 'Untitled'}</p>
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'No date'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Related Items (for non-Time Gaps insights) */}
                {!isAIPredictions && typeKey !== 'time_gaps' && (relatedItems.length > 0 || suggestion.relatedItems?.length > 0) && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Related items
                    </h3>
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {(suggestion.relatedItems || relatedItems).slice(0, 5).map((item, i) => {
                        const thumbUrl = getThumbnailUrl(item) || item.thumbnail_url || item.image_url || item.url;
                        const proxiedUrl = thumbUrl ? getImageUrl({ image: thumbUrl, thumbnail_url: thumbUrl }) : null;
                        
                        return (
                          <div 
                            key={item.id || i} 
                            className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-purple-100 to-violet-100"
                          >
                            {proxiedUrl ? (
                              <img src={proxiedUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-purple-400">
                                <Layers size={14} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Other Suggestions */}
                {otherSuggestions.length > 0 && (
                  <div className="pt-3 border-t border-gray-100">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Other insights
                    </h3>
                    <div className="space-y-1">
                      {otherSuggestions.slice(0, 2).map((s, i) => {
                        const sTypeKey = typeof s.type === 'string' ? s.type : (s.type?.id || 'none');
                        const sConfig = SUGGESTION_CONFIG[sTypeKey] || SUGGESTION_CONFIG.none;
                        const SIcon = sConfig.icon;
                        return (
                          <div 
                            key={s.id || i} 
                            onClick={() => onSelectSuggestion?.(s)}
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${sConfig.gradient} flex items-center justify-center`}>
                              <SIcon size={11} className="text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 line-clamp-1">{s.title}</p>
                            </div>
                            <ChevronRight size={12} className="text-gray-300" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions - Type-specific action buttons */}
              <div className="px-5 py-3 border-t border-gray-100 bg-white flex-shrink-0">
                {typeKey === 'neglected_focus' ? (
                  // ============================================
                  // NEGLECTED FOCUS ACTIONS
                  // ============================================
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={handleOpenFocus}
                        disabled={actionLoading}
                        className={`flex-1 py-2 px-3 bg-gradient-to-r ${config.gradient} text-white text-xs font-medium rounded-lg transition-all hover:shadow-md flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <Target size={13} />
                        Open Focus
                      </button>
                      <button
                        onClick={handleArchiveFocus}
                        disabled={actionLoading}
                        className="flex-1 py-2 px-3 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Archive size={13} />
                        Archive Focus
                      </button>
                      <button
                        onClick={handleMakePlan}
                        disabled={actionLoading}
                        className="flex-1 py-2 px-3 bg-gray-900 hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <MessageCircle size={13} />
                        Make a plan
                      </button>
                    </div>
                  </>
                ) : typeKey === 'time_gaps' ? (
                  // ============================================
                  // TIME GAPS ACTIONS
                  // ============================================
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={handleViewRelatedItems}
                        disabled={actionLoading}
                        className={`flex-1 py-2 px-3 ${showRelatedItems ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gradient-to-r ' + config.gradient} text-white text-xs font-medium rounded-lg transition-all hover:shadow-md flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <List size={13} />
                        {showRelatedItems ? 'Hide items' : 'View related items'}
                      </button>
                      <button
                        onClick={handleCreateFocusFromTheme}
                        disabled={actionLoading}
                        className="flex-1 py-2 px-3 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Target size={13} />
                        Create focus from theme
                      </button>
                      <button
                        onClick={handleAskSwirlAboutGap}
                        disabled={actionLoading}
                        className="flex-1 py-2 px-3 bg-gray-900 hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <MessageCircle size={13} />
                        Ask Swirl about gap
                      </button>
                    </div>
                  </>
                ) : isAIPredictions ? (
                  // ============================================
                  // AI PREDICTIONS (Coming Soon)
                  // ============================================
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={handleViewPredictions}
                        className={`flex-1 py-2 px-3 bg-gradient-to-r ${config.gradient} text-white text-xs font-medium rounded-lg transition-all hover:shadow-md flex items-center justify-center gap-1.5`}
                      >
                        <Brain size={13} />
                        Coming Soon
                      </button>
                    </div>
                  </>
                ) : (
                  // ============================================
                  // DEFAULT ACTIONS (Deep Dive, Evening Pattern, Emerging Direction)
                  // ============================================
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={handleCreateFocus}
                        className={`flex-1 py-2 px-3 bg-gradient-to-r ${config.gradient} text-white text-xs font-medium rounded-lg transition-all hover:shadow-md flex items-center justify-center gap-1.5`}
                      >
                        <Target size={13} />
                        Create a Focus
                      </button>
                      <button
                        onClick={handleAskSwirl}
                        className="flex-1 py-2 px-3 bg-gray-900 hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5"
                      >
                        <MessageCircle size={13} />
                        Go deeper
                      </button>
                    </div>
                  </>
                )}
                
                {/* Utility Buttons - Pin/Hide (not shown for auto-pinned insights) */}
                {!isAIPredictions && typeKey !== 'neglected_focus' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => isPinned ? onUnpin?.(suggestion.id) : onPin?.(suggestion)}
                      className={`flex-1 py-1.5 px-2 text-[11px] font-medium rounded-md transition-colors flex items-center justify-center gap-1 ${
                        isPinned 
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Bookmark size={10} className={isPinned ? 'fill-current' : ''} />
                      {isPinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button
                      onClick={() => { onDismiss?.(suggestion.id); onClose(); }}
                      className="flex-1 py-1.5 px-2 bg-gray-100 text-gray-500 hover:text-red-600 hover:bg-red-50 text-[11px] font-medium rounded-md transition-colors flex items-center justify-center gap-1"
                    >
                      <EyeOff size={10} />
                      Hide
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
